import { LocalStorageKey } from 'shared/constants/localStorage'
import { DesktopGridItem } from 'shared/types/desktop-grid'
import { lsGetItem } from 'shared/utils/local-storage'
import { create } from 'zustand'

import { CellSize } from '../components/DesktopGrid/types/grid.types'

type SetItemsAction =
  | DesktopGridItem[]
  | ((prevItems: DesktopGridItem[]) => DesktopGridItem[])

interface DesktopStoreState {
  items: DesktopGridItem[]
  highlightedShortcutId: number | null
  gridSize: {
    cols: number
    rows: number
  }
  cellSize: CellSize
  offset: { x: number; y: number }
}

interface DesktopStoreActions {
  setItems: (value: SetItemsAction) => void
  clearItems: () => void
  highlightShortcut: (fileId: number) => void
  setGridSize: (cols: number, rows: number) => void
  setCellSize: (size: CellSize) => void
  setOffset: (offset: { x: number; y: number }) => void
}

export type DesktopStore = DesktopStoreState & DesktopStoreActions

const SHORTCUT_HIGHLIGHT_DURATION_MS = 2000
let shortcutHighlightTimeout: ReturnType<typeof setTimeout> | undefined

export const useDesktopStore = create<DesktopStore>(set => ({
  items:
    lsGetItem(LocalStorageKey.DESKTOP_GRID)?.[
      lsGetItem(LocalStorageKey.USER)?.id ?? ''
    ] ?? [],
  highlightedShortcutId: null,

  gridSize: {
    cols: 0,
    rows: 0
  },

  cellSize: { width: 0, height: 0, gapX: 0, gapY: 0 },

  offset: { x: 0, y: 0 },

  setItems: value => {
    // this is because we want to make it behave like react's setState
    if (typeof value === 'function') {
      set(state => ({
        items: value(state.items)
      }))
      return
    }
    set({ items: value })
  },

  clearItems: () => {
    if (shortcutHighlightTimeout) clearTimeout(shortcutHighlightTimeout)
    shortcutHighlightTimeout = undefined
    set({ items: [], highlightedShortcutId: null })
  },

  highlightShortcut: fileId => {
    if (shortcutHighlightTimeout) clearTimeout(shortcutHighlightTimeout)
    set({ highlightedShortcutId: fileId })
    shortcutHighlightTimeout = setTimeout(() => {
      set(state =>
        state.highlightedShortcutId === fileId
          ? { highlightedShortcutId: null }
          : {}
      )
      shortcutHighlightTimeout = undefined
    }, SHORTCUT_HIGHLIGHT_DURATION_MS)
  },

  setGridSize: (cols, rows) =>
    set({
      gridSize: {
        cols,
        rows
      }
    }),

  setCellSize: cellSize => set({ cellSize }),

  setOffset: offset => set({ offset })
}))
